/**
 * Utility functions for exporting and importing sections as base64-encoded files.
 * Handles encoding/decoding, validation, and file download operations.
 */

import { isVersionCompatible, getVersionError, VERSION_KEY } from '../config/pageBuilderVersion';

/**
 * Fields to exclude when exporting a section.
 * These are server-generated or context-dependent fields.
 */
const EXCLUDED_EXPORT_FIELDS = ['created_at', 'updated_at', 'order_index', 'page_id'];

/**
 * Required fields that must be present in a valid section.
 */
const REQUIRED_SECTION_FIELDS = ['section_type', 'content'];

/**
 * Encodes a string to base64 with proper Unicode support.
 * @param {string} str - The string to encode
 * @returns {string} Base64-encoded string
 */
function encodeToBase64(str) {
  // Handle Unicode characters by first encoding to UTF-8
  const utf8Encoded = encodeURIComponent(str).replace(
    /%([0-9A-F]{2})/g,
    (_, p1) => String.fromCharCode(parseInt(p1, 16))
  );
  return btoa(utf8Encoded);
}

/**
 * Decodes a base64 string with proper Unicode support.
 * @param {string} base64 - The base64 string to decode
 * @returns {string} Decoded string
 */
function decodeFromBase64(base64) {
  const binaryStr = atob(base64);
  // Decode UTF-8 characters
  const utf8Decoded = binaryStr
    .split('')
    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
    .join('');
  return decodeURIComponent(utf8Decoded);
}

/**
 * Validates that the given object has the required structure for a section.
 * Also checks version compatibility when checkVersion is true.
 * @param {Object} section - The section object to validate
 * @param {Object} options - Validation options
 * @param {boolean} [options.checkVersion=true] - Whether to check version compatibility
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }} Validation result with any errors and warnings
 */
export function validateSectionStructure(section, options = {}) {
  const { checkVersion = true } = options;
  const errors = [];
  const warnings = [];

  // Check if section is an object
  if (!section || typeof section !== 'object') {
    return {
      valid: false,
      errors: ['Section must be a non-null object'],
      warnings,
    };
  }

  // Check for required fields
  for (const field of REQUIRED_SECTION_FIELDS) {
    if (!(field in section)) {
      errors.push(`Missing required field: ${field}`);
    }
  }

  // Validate section_type is a non-empty string
  if (section.section_type !== undefined) {
    if (typeof section.section_type !== 'string' || section.section_type.trim() === '') {
      errors.push('section_type must be a non-empty string');
    }
  }

  // Validate content is an object
  if (section.content !== undefined) {
    if (typeof section.content !== 'object' || section.content === null) {
      errors.push('content must be a non-null object');
    }
  }

  // Check version compatibility if requested
  if (checkVersion && section.content) {
    const sectionVersion = section.content[VERSION_KEY];
    if (sectionVersion && !isVersionCompatible(sectionVersion)) {
      errors.push(getVersionError(sectionVersion, 'This section'));
    } else if (!sectionVersion) {
      // No version is OK (legacy section), but we can note it
      warnings.push('Section has no version tag (legacy format). It will be updated to the current version on save.');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Exports a section object to a base64-encoded string.
 * Removes server-generated fields before encoding.
 * @param {Object} section - The section object to export
 * @returns {string} Base64-encoded section data
 * @throws {Error} If section is invalid
 */
export function exportSectionToBase64(section) {
  if (!section || typeof section !== 'object') {
    throw new Error('Section must be a valid object');
  }

  // Create a shallow copy and remove excluded fields
  const exportData = { ...section };
  for (const field of EXCLUDED_EXPORT_FIELDS) {
    delete exportData[field];
  }

  // Convert to JSON and encode to base64
  const jsonString = JSON.stringify(exportData);
  return encodeToBase64(jsonString);
}

/**
 * Imports a section from a base64-encoded string.
 * Validates the structure before returning.
 * @param {string} base64String - The base64-encoded section data
 * @returns {Object} The parsed and validated section object
 * @throws {Error} If decoding fails or section structure is invalid
 */
export function importSectionFromBase64(base64String) {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Input must be a non-empty string');
  }

  let decoded;
  try {
    decoded = decodeFromBase64(base64String);
  } catch (e) {
    throw new Error('Failed to decode base64 string: ' + e.message);
  }

  let parsed;
  try {
    parsed = JSON.parse(decoded);
  } catch (e) {
    throw new Error('Failed to parse JSON: ' + e.message);
  }

  // Validate the parsed section structure
  const validation = validateSectionStructure(parsed);
  if (!validation.valid) {
    throw new Error('Invalid section structure: ' + validation.errors.join(', '));
  }

  return parsed;
}

/**
 * Generates a filename for exporting a section.
 * Format: section-{name}-{YYYY-MM-DD}.section
 * @param {Object} section - The section object
 * @returns {string} The generated filename
 */
export function generateExportFilename(section) {
  // Get section name from content or use section_type as fallback
  let name = section?.content?.name || section?.section_type || 'unnamed';

  // Sanitize the name for use in filename
  // Replace spaces with hyphens and remove invalid characters
  name = name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '')
    .substring(0, 50); // Limit length

  // Format date as YYYY-MM-DD
  const date = new Date().toISOString().split('T')[0];

  return `section-${name}-${date}.section`;
}

/**
 * Creates and triggers a download of a section as a .section file.
 * @param {Object} section - The section object to download
 * @param {string} [filename] - Optional custom filename. If not provided, one will be generated.
 * @throws {Error} If section is invalid or download fails
 */
export function downloadSectionAsFile(section, filename) {
  // Export section to base64
  const base64Data = exportSectionToBase64(section);

  // Use provided filename or generate one
  const downloadFilename = filename || generateExportFilename(section);

  // Create a Blob with the base64 data
  const blob = new Blob([base64Data], { type: 'application/octet-stream' });

  // Create a temporary URL for the blob
  const url = URL.createObjectURL(blob);

  // Create a hidden anchor element and trigger download
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = downloadFilename;
  anchor.style.display = 'none';

  // Append to body, click, and cleanup
  document.body.appendChild(anchor);
  anchor.click();

  // Cleanup: remove anchor and revoke URL
  // Use setTimeout to ensure the download starts before cleanup
  setTimeout(() => {
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Recursively counts all components in a section's content.
 * Traverses through all nested children arrays.
 * @param {Object} section - The section object
 * @returns {number|null} The total component count, or null if not applicable
 */
export function countAllComponents(section) {
  if (!section || section.section_type !== 'layout-1' || !section.content?.components) {
    return null;
  }

  function countRecursive(components) {
    if (!Array.isArray(components)) return 0;

    let count = 0;
    for (const component of components) {
      count++; // Count this component
      if (component.children && Array.isArray(component.children)) {
        count += countRecursive(component.children); // Count all children recursively
      }
    }
    return count;
  }

  return countRecursive(section.content.components);
}

/**
 * Reads a .section file and returns the parsed section object.
 * Useful for handling file input events.
 * @param {File} file - The file object from an input element
 * @returns {Promise<Object>} Promise resolving to the parsed section
 */
export async function readSectionFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const base64Content = event.target.result;
        const section = importSectionFromBase64(base64Content);
        resolve(section);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}
